import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Sparkles, TrendingUp, Wrench, Plus, Pencil, Trash2, Loader2, Megaphone } from 'lucide-react';
import { formatRomeDateTime } from '../lib/datetime';

export const TAG_META = {
  feature: { label: 'Nuova funzionalità', icon: Sparkles, classes: 'bg-emerald-100 text-emerald-800 border-emerald-200', dotClass: 'bg-emerald-500' },
  improvement: { label: 'Miglioramento', icon: TrendingUp, classes: 'bg-blue-100 text-blue-800 border-blue-200', dotClass: 'bg-blue-500' },
  bugfix: { label: 'Correzione bug', icon: Wrench, classes: 'bg-amber-100 text-amber-800 border-amber-200', dotClass: 'bg-amber-500' },
};

export const PatchNoteCard = ({ note, canEdit, onEdit, onDelete }) => {
  const meta = TAG_META[note.tag] || TAG_META.feature;
  const Icon = meta.icon;
  return (
    <Card className="border-slate-200 hover:border-slate-300 transition-colors" data-testid={`patch-note-card-${note.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-bold ${meta.classes}`}>
              <Icon className="w-3 h-3" />
              {meta.label}
            </span>
            {note.version && (
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600">v{note.version}</span>
            )}
            <span className="text-xs text-slate-400">{formatRomeDateTime(note.created_at)}</span>
          </div>
          {canEdit && (
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={() => onEdit(note)} className="h-7 w-7 p-0" title="Modifica" data-testid={`edit-patch-${note.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onDelete(note)} className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" title="Elimina" data-testid={`delete-patch-${note.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>
        <h3 className="font-bold text-slate-900 mb-1">{note.title}</h3>
        <p className="text-sm text-slate-600 whitespace-pre-wrap">{note.description}</p>
        {note.created_by_name && (
          <p className="text-xs text-slate-400 mt-2">— {note.created_by_name}</p>
        )}
      </CardContent>
    </Card>
  );
};

const emptyForm = { title: '', description: '', tag: 'feature', version: '' };

const PatchNotesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'super_admin';

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTag, setFilterTag] = useState('all');

  // Edit/Create dialog
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);  // null = create mode
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [toDelete, setToDelete] = useState(null);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/patch-notes');
      setNotes(data);
    } catch (e) {
      toast.error('Errore nel caricamento delle novità');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotes();
    // Marca le notifiche "patch_note" come lette quando l'utente apre la pagina
    api.post('/notifications/read-by-type/patch_note').catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setEditorOpen(true);
  };

  const openEdit = (note) => {
    setEditing(note);
    setForm({
      title: note.title,
      description: note.description,
      tag: note.tag,
      version: note.version || '',
    });
    setEditorOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Titolo e descrizione richiesti');
      return;
    }
    try {
      setSaving(true);
      if (editing) {
        await api.put(`/patch-notes/${editing.id}`, form);
        toast.success('Novità aggiornata');
      } else {
        await api.post('/patch-notes', form);
        toast.success('Novità pubblicata');
      }
      setEditorOpen(false);
      await loadNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/patch-notes/${toDelete.id}`);
      toast.success('Novità eliminata');
      setToDelete(null);
      await loadNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const filtered = filterTag === 'all' ? notes : notes.filter(n => n.tag === filterTag);

  return (
    <div className="space-y-6" data-testid="patch-notes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-blue-700" />
            Novità & Aggiornamenti
          </h1>
          <p className="text-slate-500 mt-1">Tutte le ultime modifiche all'applicazione</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filterTag} onValueChange={setFilterTag}>
            <SelectTrigger className="w-48" data-testid="patch-filter-tag">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              <SelectItem value="feature">Nuova funzionalità</SelectItem>
              <SelectItem value="improvement">Miglioramento</SelectItem>
              <SelectItem value="bugfix">Correzione bug</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && (
            <Button onClick={openCreate} className="bg-blue-900 hover:bg-blue-800 gap-2" data-testid="create-patch-button">
              <Plus className="w-4 h-4" />
              Nuova novità
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-slate-300">
          <CardContent className="p-12 text-center">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nessuna novità pubblicata{filterTag !== 'all' ? ' di questo tipo' : ''}.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="patch-notes-list">
          {filtered.map(note => (
            <PatchNoteCard key={note.id} note={note} canEdit={isAdmin} onEdit={openEdit} onDelete={setToDelete} />
          ))}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica novità' : 'Pubblica nuova novità'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Aggiorna il contenuto della patch note' : 'Le novità sono visibili a tutti gli utenti e generano una notifica automatica'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select value={form.tag} onValueChange={(v) => setForm({ ...form, tag: v })}>
                <SelectTrigger data-testid="patch-form-tag">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="feature">🟢 Nuova funzionalità</SelectItem>
                  <SelectItem value="improvement">🔵 Miglioramento</SelectItem>
                  <SelectItem value="bugfix">🟡 Correzione bug</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2 col-span-2">
                <Label>Titolo *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Es. Scheda tecnico PDF"
                  required
                  data-testid="patch-form-title"
                />
              </div>
              <div className="space-y-2">
                <Label>Versione</Label>
                <Input
                  value={form.version}
                  onChange={(e) => setForm({ ...form, version: e.target.value })}
                  placeholder="1.5"
                  data-testid="patch-form-version"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descrizione *</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Cosa è cambiato? Sii chiaro per i tuoi colleghi."
                rows={5}
                required
                data-testid="patch-form-description"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditorOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="patch-form-submit">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editing ? 'Aggiorna' : 'Pubblica'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa novità?</AlertDialogTitle>
            <AlertDialogDescription>
              "{toDelete?.title}" non sarà più visibile a nessun utente. L'azione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-patch">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PatchNotesPage;
